import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface Props {
  className?: string;
  variant?: 'light' | 'dark' | 'sidebar' | 'compact';
}

const LanguageSwitcher = ({ className, variant = 'dark' }: Props) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const toggleLanguage = () => {
    const newLang = currentLanguage === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  // Compact variant for mobile - circular button, cycles on tap
  if (variant === 'compact') {
    return (
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={toggleLanguage}
        aria-label={`Language: ${currentLanguage.toUpperCase()}. Tap to switch.`}
        className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-border-subtle bg-surface-card transition-colors hover:border-accent-primary ${className ?? ''}`}
      >
        <span className="font-body text-xs font-semibold text-foreground-primary">
          {currentLanguage.toUpperCase()}
        </span>
      </motion.button>
    );
  }

  // Sidebar variant - pill buttons for dark sidebar
  if (variant === 'sidebar') {
    return (
      <div
        className={`inline-flex w-fit rounded-lg bg-[#2A2A2A] ${className ?? ''}`}
      >
        <button
          type="button"
          onClick={() => handleLanguageChange('en')}
          className={`rounded-lg px-4 py-2 text-[13px] font-semibold transition-colors ${
            currentLanguage === 'en'
              ? 'bg-accent-primary text-surface-inverse'
              : 'text-foreground-inverse'
          }`}
        >
          EN
        </button>
        <button
          type="button"
          onClick={() => handleLanguageChange('fr')}
          className={`rounded-lg px-4 py-2 text-[13px] font-medium transition-colors ${
            currentLanguage === 'fr'
              ? 'bg-accent-primary text-surface-inverse'
              : 'text-foreground-inverse'
          }`}
        >
          FR
        </button>
      </div>
    );
  }

  // Toggle variant for light/dark backgrounds
  const isLight = variant === 'light';

  const buttonStyles = isLight
    ? 'border-white/30 bg-transparent hover:border-white/60'
    : 'border-border-subtle bg-surface-card hover:border-accent-primary';

  const activeTextStyles = isLight ? 'text-white' : 'text-foreground-primary';
  const inactiveTextStyles = isLight
    ? 'text-white/50'
    : 'text-foreground-tertiary';
  const separatorStyles = isLight
    ? 'text-white/50'
    : 'text-foreground-tertiary';

  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={toggleLanguage}
      className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 transition-colors ${buttonStyles} ${className}`}
    >
      <span
        className={`font-body text-sm font-medium ${
          currentLanguage === 'en' ? activeTextStyles : inactiveTextStyles
        }`}
      >
        EN
      </span>
      <span className={separatorStyles}>|</span>
      <span
        className={`font-body text-sm font-medium ${
          currentLanguage === 'fr' ? activeTextStyles : inactiveTextStyles
        }`}
      >
        FR
      </span>
    </motion.button>
  );
};

export default LanguageSwitcher;
